import { Alert } from "@/src/lib/productAlert";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { IdentityHeader } from "@/src/components/IdentityHeader";
import { ReturnToLatestButton } from "@/src/components/ReturnToLatestButton";
import { Ionicons } from "@/src/components/ui/ionicons";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { usePublicProfile } from "@/src/hooks/usePublicProfile";
import { useConversationScroll } from "@/src/hooks/useConversationScroll";
import { createChatMessageId, getChatErrorMessage } from "@/src/lib/chat";
import {
  canClaimDraftSubmission,
  mergeIdentityDmMessages,
  normalizeDirectMessageText,
  optimisticIdentityDmMessage,
  shouldSubmitComposerKey,
  updateLocalMessageStatus,
} from "@/src/lib/directMessageOptimistic";
import {
  listIdentityDmMessages,
  listIdentityDmMessagesBefore,
  markIdentityDmRead,
  sendIdentityDmMessage,
  subscribeIdentityDmMessages,
  type IdentityDmConversation,
  type IdentityDmMessage,
} from "@/src/lib/directMessaging";
import {
  blockIdentity,
  getIdentityBlockStatus,
  reportIdentityMessaging,
  unblockIdentity,
} from "@/src/lib/identityMessagingSafety";
import { buildPublicIdentitySnapshot, getIdentityBadges, getAccountType } from "@/src/lib/identity";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import type { PublicIdentitySnapshot } from "@/src/types/identity";
import { useIsFocused } from "@react-navigation/native";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, FlatList, KeyboardAvoidingView, Platform, Pressable, RefreshControl, TextInput as NativeTextInput, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const CARD = surface.primary;
const BORDER = border.subtle;
const ACCENT = brand.primary;
const MUTED = uiText.muted;
const INITIAL_MESSAGE_LIMIT = 100;

function formatTime(ms: number) {
  return ms ? new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
}

function SharedPostBubble({ message }: { message: IdentityDmMessage }) {
  const sharedPost = message.sharedPost;
  if (!sharedPost?.postId) return <Text style={{ color: uiText.primary }}>Publication partagée</Text>;
  return (
    <Pressable onPress={() => router.push(`/post/${sharedPost.postId}`)} accessibilityRole="button" accessibilityLabel="Ouvrir la publication partagée" style={({ pressed }) => ({ minWidth: 220, maxWidth: 320, borderRadius: 14, borderWidth: 1, borderColor: BORDER, backgroundColor: surface.overlay, padding: 12, opacity: pressed ? 0.78 : 1, gap: 10 })}>
      <Text style={{ color: uiText.muted, fontWeight: "800", fontSize: 12 }}>Publication partagée</Text>
      {sharedPost.authorSnapshot ? <IdentityHeader identity={sharedPost.authorSnapshot} compact /> : null}
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}><Text style={{ color: uiText.brand, fontWeight: "900", flex: 1 }}>Voir la publication</Text><Ionicons name="chevron-forward" size={16} color={uiText.brand} /></View>
    </Pressable>
  );
}

export default function IdentityDirectRoom() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const conversationId = String(id || "");
  const { activeAccountId, activeAccount, refresh: refreshAccounts } = useActiveAccount();
  const isFocused = useIsFocused();
  const [conversation, setConversation] = useState<IdentityDmConversation | null>(null);
  const [messages, setMessages] = useState<IdentityDmMessage[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderContextKey, setRenderContextKey] = useState("");
  const [peerBlocked, setPeerBlocked] = useState(false);
  const [safetyBusy, setSafetyBusy] = useState(false);
  const composerRef = useRef<NativeTextInput>(null);
  const draftVersionRef = useRef(0);
  const lastSubmittedDraftVersionRef = useRef<number | null>(null);
  const inFlightMessageIdsRef = useRef(new Set<string>());
  const lastReadMessageIdRef = useRef<string | null>(null);
  const activeAccountIdRef = useRef<string | null>(activeAccountId);
  const conversationIdRef = useRef(conversationId);
  const revokedRecoveryRef = useRef("");
  activeAccountIdRef.current = activeAccountId;
  conversationIdRef.current = conversationId;

  const currentContextKey = activeAccountId && conversationId ? `${activeAccountId}:${conversationId}` : "";
  const contextIsCurrent = !!currentContextKey && renderContextKey === currentContextKey;
  const visibleConversation = contextIsCurrent ? conversation : null;
  const visibleMessages = useMemo(() => contextIsCurrent ? messages : [], [contextIsCurrent, messages]);
  const visibleDenied = contextIsCurrent && denied;
  const visibleError = contextIsCurrent ? error : null;
  const visibleRefreshing = contextIsCurrent && refreshing;
  const visibleLoadingOlder = contextIsCurrent && loadingOlder;
  const visibleHasMore = contextIsCurrent && hasMore;
  const newestMessage = visibleMessages.at(-1);
  const inverted = Platform.OS !== "web";
  const listMessages = useMemo(() => inverted ? [...visibleMessages].reverse() : visibleMessages, [inverted, visibleMessages]);
  const { listRef, onScroll, onLayout, onContentSizeChange, returnToLatest, showReturnToLatest, followLatest } =
    useConversationScroll<IdentityDmMessage>(currentContextKey, newestMessage?.id, inverted);

  const isCurrentRequest = useCallback((identityId: string, targetConversationId: string) => (
    activeAccountIdRef.current === identityId && conversationIdRef.current === targetConversationId
  ), []);

  const activeIdentityParticipates = useMemo(() => !!activeAccountId && !!visibleConversation?.participantIdentityIds?.includes(activeAccountId), [activeAccountId, visibleConversation?.participantIdentityIds]);
  const peerIdentityId = useMemo(() => {
    if (!activeAccountId || !activeIdentityParticipates || !visibleConversation?.participantIdentityIds) return "";
    return visibleConversation.participantIdentityIds.find((identityId) => identityId !== activeAccountId) || "";
  }, [activeAccountId, activeIdentityParticipates, visibleConversation?.participantIdentityIds]);
  const { profile: peerProfile } = usePublicProfile(peerIdentityId);

  const peerSnapshot = useMemo<PublicIdentitySnapshot | null>(() => {
    if (!peerIdentityId) return null;
    for (let index = visibleMessages.length - 1; index >= 0; index -= 1) {
      const snapshot = visibleMessages[index]?.senderSnapshot;
      if (snapshot?.identityId === peerIdentityId) return snapshot;
    }
    const conversationSnapshot = visibleConversation?.participantSnapshots?.[peerIdentityId];
    if (conversationSnapshot?.identityId === peerIdentityId) return conversationSnapshot;
    if (!peerProfile) return null;
    const accountType = getAccountType(peerProfile);
    if (!accountType) return null;
    return {
      identityId: peerIdentityId,
      handle: peerProfile.username || "",
      displayName: peerProfile.displayName || peerProfile.institutionName || peerProfile.username || "Identité Neqraw",
      avatarUrl: peerProfile.photoURL || null,
      accountType,
      badges: peerProfile.badges?.length ? peerProfile.badges : getIdentityBadges(peerProfile),
    };
  }, [peerIdentityId, peerProfile, visibleConversation?.participantSnapshots, visibleMessages]);

  useEffect(() => {
    if (!peerIdentityId) {
      setPeerBlocked(false);
      return;
    }
    let active = true;
    void getIdentityBlockStatus(peerIdentityId).then((value) => { if (active) setPeerBlocked(value); }).catch(() => {});
    return () => { active = false; };
  }, [peerIdentityId]);

  const applyResult = useCallback((result: { conversation: IdentityDmConversation; messages: IdentityDmMessage[] }, identityId: string, targetConversationId: string, replace: boolean) => {
    if (!isCurrentRequest(identityId, targetConversationId)) return false;
    const requestContextKey = `${identityId}:${targetConversationId}`;
    if (!result.conversation.participantIdentityIds.includes(identityId)) {
      setDenied(true); setConversation(null); setMessages([]); setHasMore(false); setRenderContextKey(requestContextKey); return false;
    }
    setDenied(false);
    setConversation(result.conversation);
    setMessages((current) => replace ? result.messages : mergeIdentityDmMessages(current, result.messages));
    if (replace) setHasMore(result.messages.length >= INITIAL_MESSAGE_LIMIT);
    setRenderContextKey(requestContextKey);
    return true;
  }, [isCurrentRequest]);

  const load = useCallback(async (mode: "initial" | "refresh" | "silent" = "initial") => {
    const identityId = activeAccountId;
    const targetConversationId = conversationId;
    if (!identityId || !targetConversationId) return;
    if (mode === "refresh") setRefreshing(true);
    if (mode === "initial") setLoading(true);
    if (mode !== "silent") setError(null);
    try {
      const result = await listIdentityDmMessages(identityId, targetConversationId);
      if (!applyResult(result, identityId, targetConversationId, mode === "initial")) return;
      await markIdentityDmRead(identityId, targetConversationId).catch(() => {});
      if (!isCurrentRequest(identityId, targetConversationId)) return;
      lastReadMessageIdRef.current = result.messages.at(-1)?.id ?? null;
    } catch (e: any) {
      if (!isCurrentRequest(identityId, targetConversationId)) return;
      const requestContextKey = `${identityId}:${targetConversationId}`;
      const permissionDenied = e?.code === "functions/permission-denied" || e?.code === "permission-denied";
      if (permissionDenied) {
        if (revokedRecoveryRef.current !== requestContextKey) {
          revokedRecoveryRef.current = requestContextKey;
          await refreshAccounts().catch(() => {});
          if (conversationIdRef.current === targetConversationId) router.replace("/(tabs)/messages");
          return;
        }
        setDenied(true); setConversation(null); setMessages([]); setHasMore(false); setRenderContextKey(requestContextKey);
      }
      if (mode !== "silent") { setError(getChatErrorMessage(e, "Impossible de charger la discussion.")); setRenderContextKey(requestContextKey); }
    } finally {
      if (isCurrentRequest(identityId, targetConversationId)) {
        if (mode === "initial") setLoading(false);
        if (mode === "refresh") setRefreshing(false);
      }
    }
  }, [activeAccountId, applyResult, conversationId, isCurrentRequest, refreshAccounts]);

  const loadOlder = useCallback(async () => {
    const identityId = activeAccountId;
    const targetConversationId = conversationId;
    const beforeMessageId = visibleMessages[0]?.id;
    if (!identityId || !targetConversationId || !contextIsCurrent || !beforeMessageId || loadingOlder || !hasMore) return;
    try {
      setLoadingOlder(true);
      const result = await listIdentityDmMessagesBefore(identityId, targetConversationId, beforeMessageId);
      if (!isCurrentRequest(identityId, targetConversationId)) return;
      setMessages((current) => mergeIdentityDmMessages(result.messages, current));
      setHasMore(result.hasMore);
    } catch (e: any) {
      if (isCurrentRequest(identityId, targetConversationId)) setError(getChatErrorMessage(e, "Impossible de charger les anciens messages."));
    } finally {
      if (isCurrentRequest(identityId, targetConversationId)) setLoadingOlder(false);
    }
  }, [activeAccountId, contextIsCurrent, conversationId, hasMore, isCurrentRequest, loadingOlder, visibleMessages]);

  useEffect(() => {
    revokedRecoveryRef.current = "";
    lastReadMessageIdRef.current = null;
    setConversation(null); setMessages([]); setText(""); setDenied(false); setHasMore(false); setLoadingOlder(false); setRefreshing(false); setRenderContextKey("");
    draftVersionRef.current = 0;
    lastSubmittedDraftVersionRef.current = null;
    inFlightMessageIdsRef.current.clear();
    if (isFocused && activeAccountId) void load("initial");
  }, [activeAccountId, conversationId, isFocused, load]);

  useEffect(() => {
    if (!isFocused || !activeAccountId || !conversationId || !contextIsCurrent || !activeIdentityParticipates || visibleDenied) return;
    const identityId = activeAccountId;
    const targetConversationId = conversationId;
    return subscribeIdentityDmMessages(targetConversationId, (incoming) => {
      if (!isCurrentRequest(identityId, targetConversationId)) return;
      setMessages((current) => mergeIdentityDmMessages(current, incoming));
      const latest = incoming.at(-1);
      if (!latest) return;
      setConversation((current) => current ? {
        ...current,
        lastMessageText: latest.text,
        lastMessageAt: latest.createdAt,
        lastMessageSenderIdentityId: latest.senderIdentityId,
      } : current);
      if (latest.senderIdentityId !== identityId && lastReadMessageIdRef.current !== latest.id) {
        lastReadMessageIdRef.current = latest.id;
        void markIdentityDmRead(identityId, targetConversationId).catch(() => {
          if (isCurrentRequest(identityId, targetConversationId)
            && lastReadMessageIdRef.current === latest.id) {
            lastReadMessageIdRef.current = null;
          }
        });
      }
    }, (listenerError) => {
      if (isCurrentRequest(identityId, targetConversationId)) {
        setError(getChatErrorMessage(listenerError, "La mise à jour des messages a été interrompue."));
      }
    });
  }, [activeAccountId, activeIdentityParticipates, contextIsCurrent, conversationId, isCurrentRequest, isFocused, visibleDenied]);

  const sendMessage = useCallback(async (message: IdentityDmMessage) => {
    const identityId = activeAccountId;
    const targetConversationId = conversationId;
    if (!identityId || message.senderIdentityId !== identityId || inFlightMessageIdsRef.current.has(message.id)) return;
    inFlightMessageIdsRef.current.add(message.id);
    try {
      await sendIdentityDmMessage({ conversationId: targetConversationId, senderIdentityId: identityId, messageId: message.id, text: message.text, type: "text" });
    } catch (e: any) {
      if (!isCurrentRequest(identityId, targetConversationId)) return;
      setMessages((current) => updateLocalMessageStatus(current, message.id, "failed"));
      setError(getChatErrorMessage(e, "Impossible d'envoyer le message."));
    } finally {
      inFlightMessageIdsRef.current.delete(message.id);
    }
  }, [activeAccountId, conversationId, isCurrentRequest]);

  const send = useCallback(() => {
    const clean = normalizeDirectMessageText(text);
    const identityId = activeAccountId;
    const draftVersion = draftVersionRef.current;
    if (!contextIsCurrent
      || !identityId
      || visibleDenied
      || peerBlocked
      || !activeIdentityParticipates
      || !canClaimDraftSubmission({ text, draftVersion, lastSubmittedVersion: lastSubmittedDraftVersionRef.current })) return;

    lastSubmittedDraftVersionRef.current = draftVersion;
    const messageId = createChatMessageId();
    const senderSnapshot = activeAccount
      ? buildPublicIdentitySnapshot(activeAccount)
      : null;
    const optimistic = optimisticIdentityDmMessage({
      messageId,
      senderIdentityId: identityId,
      senderSnapshot,
      text: clean,
      createdAt: Date.now(),
    });

    setError(null);
    followLatest(true);
    setMessages((current) => mergeIdentityDmMessages(current, [optimistic]));
    setText("");
    if (Platform.OS === "web") composerRef.current?.focus();
    void sendMessage(optimistic);
  }, [activeAccount, activeAccountId, activeIdentityParticipates, contextIsCurrent, followLatest, peerBlocked, sendMessage, text, visibleDenied]);

  const retryMessage = useCallback((message: IdentityDmMessage) => {
    if (message.localStatus !== "failed" || inFlightMessageIdsRef.current.has(message.id)) return;
    setMessages((current) => updateLocalMessageStatus(current, message.id, "sending"));
    setError(null);
    void sendMessage({ ...message, localStatus: "sending" });
  }, [sendMessage]);

  const updateDraft = useCallback((value: string) => {
    draftVersionRef.current += 1;
    setText(value);
  }, []);

  const handleComposerKeyPress = useCallback((event: any) => {
    if (Platform.OS !== "web") return;
    const nativeEvent = event.nativeEvent ?? event;
    if (!shouldSubmitComposerKey({
      key: nativeEvent.key ?? event.key,
      shiftKey: nativeEvent.shiftKey ?? event.shiftKey,
      isComposing: nativeEvent.isComposing ?? event.isComposing,
    })) return;
    event.preventDefault?.();
    nativeEvent.preventDefault?.();
    send();
  }, [send]);

  const togglePeerBlock = useCallback(async () => {
    if (!peerIdentityId || safetyBusy) return;
    try {
      setSafetyBusy(true);
      if (peerBlocked) {
        await unblockIdentity(peerIdentityId); setPeerBlocked(false); Alert.alert("Identité débloquée", "La discussion peut reprendre.");
      } else {
        await blockIdentity(peerIdentityId); setPeerBlocked(true); Alert.alert("Identité bloquée", "Aucun nouveau message ne peut être échangé avec cette identité.");
      }
    } catch (e) {
      Alert.alert("Action impossible", getChatErrorMessage(e, "Impossible de mettre à jour ce blocage."));
    } finally { setSafetyBusy(false); }
  }, [peerBlocked, peerIdentityId, safetyBusy]);

  const reportConversation = useCallback(async () => {
    if (!activeAccountId || !peerIdentityId || safetyBusy) return;
    try {
      setSafetyBusy(true);
      await reportIdentityMessaging({ reporterIdentityId: activeAccountId, targetIdentityId: peerIdentityId, conversationId, reason: "other", details: "Signalement depuis une conversation directe" });
      Alert.alert("Signalement envoyé", "La conversation sera examinée par la modération.");
    } catch (e) {
      Alert.alert("Signalement impossible", getChatErrorMessage(e, "Impossible d'envoyer ce signalement."));
    } finally { setSafetyBusy(false); }
  }, [activeAccountId, conversationId, peerIdentityId, safetyBusy]);

  const reportMessage = useCallback(async (messageId: string) => {
    if (!activeAccountId || !peerIdentityId || safetyBusy) return;
    try {
      setSafetyBusy(true);
      await reportIdentityMessaging({ reporterIdentityId: activeAccountId, targetIdentityId: peerIdentityId, conversationId, messageId, reason: "other", details: "Signalement d'un message direct" });
      Alert.alert("Message signalé", "Ce message a été transmis à la modération comme preuve.");
    } catch (e) {
      Alert.alert("Signalement impossible", getChatErrorMessage(e, "Impossible de signaler ce message."));
    } finally { setSafetyBusy(false); }
  }, [activeAccountId, conversationId, peerIdentityId, safetyBusy]);

  if (!activeAccountId) return <SafeAreaView style={{ flex: 1, backgroundColor: BG, alignItems: "center", justifyContent: "center" }}><Text style={{ color: uiText.primary }}>Aucune identité active.</Text></SafeAreaView>;

  if (visibleDenied) {
    return <SafeAreaView style={{ flex: 1, backgroundColor: BG }}><View style={{ flex: 1, padding: 20, alignItems: "center", justifyContent: "center" }}><Ionicons name="lock-closed-outline" size={30} color={MUTED} /><Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900", marginTop: 12 }}>Cette inbox n’est pas celle de l’identité active</Text><Text style={{ color: MUTED, marginTop: 8, textAlign: "center", lineHeight: 20 }}>Reviens aux messages puis ouvre une conversation appartenant à l’identité actuellement sélectionnée.</Text><Pressable onPress={() => router.replace("/(tabs)/messages")} style={{ marginTop: 18, borderRadius: 12, backgroundColor: ACCENT, paddingHorizontal: 16, paddingVertical: 12 }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>Retour aux messages</Text></Pressable></View></SafeAreaView>;
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "left", "right"]}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : Platform.OS === "android" ? "height" : undefined}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable onPress={() => router.replace("/(tabs)/messages")} accessibilityRole="button" accessibilityLabel="Retour aux messages" style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center" }}><Ionicons name="chevron-back" size={20} color={uiText.primary} /></Pressable>
          <Pressable onPress={() => peerIdentityId && router.push(`/profile/${peerIdentityId}`)} disabled={!peerIdentityId} style={{ flex: 1 }}>
            {peerSnapshot ? <IdentityHeader identity={peerSnapshot} compact /> : <View><Text style={{ color: uiText.primary, fontSize: 18, fontWeight: "900" }}>Discussion</Text><Text style={{ color: MUTED, marginTop: 2 }}>Message direct</Text></View>}
            <Text numberOfLines={1} style={{ color: MUTED, fontSize: 11, marginTop: 3 }}>Tu écris en tant que {activeAccount?.displayName || "ton identité active"}</Text>
          </Pressable>
          {peerIdentityId ? <Pressable onPress={() => Alert.alert("Discussion", "Choisis une action.", [{ text: "Annuler", style: "cancel" }, { text: "Signaler la conversation", onPress: () => void reportConversation() }, { text: peerBlocked ? "Débloquer" : "Bloquer", style: peerBlocked ? "default" : "destructive", onPress: () => void togglePeerBlock() }])} disabled={safetyBusy} accessibilityLabel="Actions de sécurité" style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, alignItems: "center", justifyContent: "center", opacity: safetyBusy ? 0.6 : 1 }}><Ionicons name="ellipsis-horizontal" size={20} color={uiText.primary} /></Pressable> : null}
        </View>

        {peerBlocked ? <View style={{ marginHorizontal: 16, marginBottom: 8, padding: 10, borderRadius: 12, borderWidth: 1, borderColor: border.danger, backgroundColor: surface.danger }}><Text style={{ color: uiText.danger, fontWeight: "800" }}>Identité bloquée — l’envoi est désactivé.</Text></View> : null}
        {visibleError ? <View style={{ marginHorizontal: 16, marginBottom: 8, padding: 10, borderRadius: 12, borderWidth: 1, borderColor: border.danger, backgroundColor: surface.danger }}><Text style={{ color: uiText.danger }}>{visibleError}</Text></View> : null}

        {(!contextIsCurrent || (loading && !visibleMessages.length)) ? <View style={{ flex: 1, alignItems: "center", justifyContent: "center", gap: 8 }}><ActivityIndicator color={ACCENT} /><Text style={{ color: MUTED }}>Chargement de la discussion…</Text></View> : (
          <View style={{ flex: 1 }}>
          <FlatList
            ref={listRef}
            data={listMessages}
            inverted={inverted}
            keyExtractor={(item) => item.id}
            refreshControl={<RefreshControl refreshing={visibleRefreshing} onRefresh={() => load("refresh")} />}
            contentContainerStyle={{ padding: 16, gap: 6, flexGrow: 1, justifyContent: visibleMessages.length ? "flex-start" : "center" }}
            maintainVisibleContentPosition={inverted ? { minIndexForVisible: 0 } : undefined}
            onScroll={onScroll}
            onLayout={onLayout}
            scrollEventThrottle={32}
            onContentSizeChange={onContentSizeChange}
            ListHeaderComponent={!inverted && visibleHasMore ? <Pressable onPress={loadOlder} disabled={visibleLoadingOlder} style={{ alignSelf: "center", marginBottom: 12, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER }}><Text style={{ color: uiText.brand, fontWeight: "900" }}>{visibleLoadingOlder ? "Chargement…" : "Charger les anciens messages"}</Text></Pressable> : null}
            ListFooterComponent={inverted && visibleHasMore ? <Pressable onPress={loadOlder} disabled={visibleLoadingOlder} style={{ alignSelf: "center", marginBottom: 12, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 12, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER }}><Text style={{ color: uiText.brand, fontWeight: "900" }}>{visibleLoadingOlder ? "Chargement…" : "Charger les anciens messages"}</Text></Pressable> : null}
            ListEmptyComponent={<View style={{ alignItems: "center" }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>Commence la discussion</Text><Text style={{ color: MUTED, marginTop: 6, textAlign: "center" }}>Tu écris avec l’identité actuellement active.</Text></View>}
            renderItem={({ item }) => {
              const mine = item.senderIdentityId === activeAccountId;
              const shared = item.type === "shared_post" && !!item.sharedPost;
              return <Pressable onLongPress={!mine ? () => Alert.alert("Message", "Choisis une action.", [{ text: "Annuler", style: "cancel" }, { text: "Signaler ce message", style: "destructive", onPress: () => void reportMessage(item.id) }]) : undefined} style={{ alignItems: mine ? "flex-end" : "flex-start", marginBottom: 6 }}>
                {shared ? <SharedPostBubble message={item} /> : <View style={{ maxWidth: "80%", backgroundColor: mine ? ACCENT : CARD, borderWidth: 1, borderColor: mine ? ACCENT : BORDER, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 10 }}><Text style={{ color: uiText.primary, lineHeight: 20 }}>{item.text}</Text></View>}
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginTop: 3 }}>
                  <Text style={{ color: item.localStatus === "failed" ? uiText.danger : MUTED, fontSize: 11 }}>
                    {item.localStatus === "sending" ? "Envoi…" : item.localStatus === "failed" ? "Non envoyé" : formatTime(item.createdAt)}
                  </Text>
                  {item.localStatus === "failed" ? <Pressable onPress={() => retryMessage(item)} accessibilityRole="button" accessibilityLabel="Réessayer l’envoi du message" hitSlop={8}><Text style={{ color: uiText.brand, fontSize: 11, fontWeight: "900" }}>Réessayer</Text></Pressable> : null}
                </View>
              </Pressable>;
            }}
          />
          <ReturnToLatestButton visible={showReturnToLatest} onPress={returnToLatest} />
          </View>
        )}

        <View style={{ borderTopWidth: 1, borderTopColor: BORDER, padding: 12, flexDirection: "row", alignItems: "flex-end", gap: 10 }}>
          <BrandTextInput ref={composerRef} value={contextIsCurrent ? text : ""} onChangeText={updateDraft} onKeyPress={handleComposerKeyPress} editable={contextIsCurrent && !loading && activeIdentityParticipates && !peerBlocked} placeholder={peerBlocked ? "Identité bloquée" : contextIsCurrent && activeIdentityParticipates ? "Écrire un message…" : "Chargement de l’identité…"} placeholderTextColor={MUTED} multiline blurOnSubmit={Platform.OS === "web" ? false : undefined} style={{ flex: 1, minHeight: 44, maxHeight: 120, color: uiText.primary, backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 12 }} />
          <Pressable onPress={send} disabled={!contextIsCurrent || !normalizeDirectMessageText(text) || !activeIdentityParticipates || peerBlocked} style={{ minWidth: 88, height: 44, borderRadius: 12, backgroundColor: contextIsCurrent && normalizeDirectMessageText(text) && activeIdentityParticipates && !peerBlocked ? ACCENT : surface.selected, alignItems: "center", justifyContent: "center" }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>Envoyer</Text></Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
